/**
 * Adapters from the bucket benchmark contract (tasks.jsonl + scores.jsonl)
 * to the AnalysisTask shape the shared analysis components consume
 * (TaskScoreDistribution, AnalysisLedger).
 *
 * Difficulty is derived HERE for benchmarks (bucket data carries no
 * difficulty by design). Thresholds mirror the API's single source of
 * truth in app/services/eval_stats.py — keep the two in lockstep.
 */

import type {
  AnalysisTask,
  AnalysisTaskDifficulty,
  BenchmarkAttribution,
  BenchmarkManifest,
  BenchmarkScore,
  BenchmarkTask,
} from "@/api/generated/model";
import { csvCell } from "@/components/models/home/contracts";
import { readStoredValue, writeStoredValue } from "@/lib/storedValue";

export const EASY_THRESHOLD = 0.67;
export const HARD_THRESHOLD = 0.33;

export function deriveDifficulty(score: number | null): AnalysisTaskDifficulty {
  if (score === null) return "unscored";
  if (score >= EASY_THRESHOLD) return "easy";
  if (score >= HARD_THRESHOLD) return "medium";
  return "hard";
}

/** Mean of a non-empty list; null for an empty one. */
function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/**
 * Join tasks with their per-model score rows into AnalysisTask rows.
 * A task's headline score is the mean of its models' mean scores (the
 * "All models · avg" convention from the Mercor console).
 */
export function toAnalysisTasks(
  tasks: BenchmarkTask[],
  scores: BenchmarkScore[],
): AnalysisTask[] {
  const byTask = new Map<string, BenchmarkScore[]>();
  for (const score of scores) {
    const rows = byTask.get(score.task_slug);
    if (rows === undefined) {
      byTask.set(score.task_slug, [score]);
    } else {
      rows.push(score);
    }
  }

  return tasks.map((task) => {
    const rows = byTask.get(task.slug) ?? [];
    const headline = mean(rows.map((row) => row.mean_score));
    return {
      id: task.slug,
      slug: task.slug,
      display_name: task.name,
      score: headline,
      difficulty: deriveDifficulty(headline),
      run_count: rows.reduce((sum, row) => sum + row.n_rollouts, 0),
      model_scores: rows
        .map((row) => ({
          model_slug: row.model,
          score: row.mean_score,
          run_count: row.n_rollouts,
        }))
        .sort((a, b) => a.model_slug.localeCompare(b.model_slug)),
      last_run_at: null,
    };
  });
}

/**
 * Provider logo for a model slug (assets copied from the Mercor console,
 * public/model-logos/). Null when no provider matches — callers render
 * no icon rather than a wrong one.
 */
export function modelLogoSrc(model: string): string | null {
  const slug = model.toLowerCase();
  if (slug.includes("gpt") || slug.includes("openai") || slug.includes("o3")) {
    return "/model-logos/openai.svg";
  }
  if (
    slug.includes("opus") ||
    slug.includes("sonnet") ||
    slug.includes("haiku") ||
    slug.includes("claude")
  ) {
    return "/model-logos/claude.svg";
  }
  if (slug.includes("kimi")) return "/model-logos/kimi.svg";
  if (slug.includes("qwen")) return "/model-logos/qwen.svg";
  if (slug.includes("gemini") || slug.includes("google"))
    return "/model-logos/google.svg";
  if (slug.includes("deepseek")) return "/model-logos/deepseek.svg";
  if (slug.includes("glm") || slug.includes("z-ai"))
    return "/model-logos/z-ai.svg";
  if (slug.includes("nvidia") || slug.includes("nemotron"))
    return "/model-logos/nvidia.svg";
  return null;
}

export interface DifficultyCounts {
  easy: number;
  medium: number;
  hard: number;
  unscored: number;
}

export function countDifficulties(tasks: AnalysisTask[]): DifficultyCounts {
  const counts: DifficultyCounts = { easy: 0, medium: 0, hard: 0, unscored: 0 };
  for (const task of tasks) {
    counts[task.difficulty] += 1;
  }
  return counts;
}

/** One failure leaf (e.g. calculation) and its share of model failures. */
export interface AttributionLeafShare {
  leaf: string;
  label: string;
  /** Model-failure RUNS of this leaf; a task with several failing runs
   * counts once per run. */
  count: number;
  /** DISTINCT tasks behind those runs — what a leaf click-through filter
   * actually shows, so the UI can reconcile the two numbers. */
  taskCount: number;
  /** Fraction of `modelFailureCount` — the denominator the UI names. */
  share: number;
}

/** One model-failure family (e.g. reasoning and process) with its leaves. */
export interface AttributionFamilyShare {
  family: string;
  label: string;
  count: number;
  share: number;
  leaves: AttributionLeafShare[];
}

/**
 * The failure-classification hierarchy over the runs in scope.
 *
 * One attribution row per run, so a model filter re-slices honestly. The
 * denominator is `modelFailureCount`: runs whose failure is attributed to
 * the model itself. Non-model issues (verifier/prompt/tool/context) and
 * unknown-evidence runs are excluded from every share — the hierarchy only
 * partitions model failures — but their counts are reported so the
 * excluded slices stay visible.
 */
export interface AttributionBreakdown {
  families: AttributionFamilyShare[];
  /** Non-model issue types (verifier, prompt, tool, environment), ranked.
   * Shares are of `nonModelIssueCount` — their own denominator, never
   * mixed into the model-failure shares. */
  nonModelIssues: AttributionLeafShare[];
  modelFailureCount: number;
  nonModelIssueCount: number;
  unknownCount: number;
  /** Classified runs in the current scope (after the model filter). */
  runCount: number;
  /** Classified runs across ALL models — distinguishes "no export at all"
   * from "the selected model was not part of the classification pass". */
  totalRunCount: number;
}

/**
 * Task slugs with at least one run of the given failure type in scope.
 * Drives the ledger's click-through filter from the Pareto rows. The id
 * space covers model-failure leaves AND non-model issue types — the two
 * taxonomies never share an id, so one selection channel serves both.
 */
export function taskSlugsForLeaf(
  attributions: BenchmarkAttribution[],
  model: string | null,
  leaf: string,
): Set<string> {
  const slugs = new Set<string>();
  for (const run of attributions) {
    if (model !== null && run.model !== model) continue;
    if (
      (run.top_level === "model_failure" && run.leaf === leaf) ||
      (run.top_level === "non_model_issue" && run.non_model_issue === leaf)
    ) {
      slugs.add(run.task_slug);
    }
  }
  return slugs;
}

interface FamilyTally {
  label: string;
  count: number;
  leaves: Map<string, { label: string; count: number; tasks: Set<string> }>;
}

/** Count one model-failure run into its family and leaf tallies. */
function tallyModelFailure(
  tallies: Map<string, FamilyTally>,
  run: BenchmarkAttribution,
): void {
  let tally = tallies.get(run.family);
  if (tally === undefined) {
    tally = { label: run.family_label, count: 0, leaves: new Map() };
    tallies.set(run.family, tally);
  }
  tally.count += 1;
  let leaf = tally.leaves.get(run.leaf);
  if (leaf === undefined) {
    leaf = { label: run.leaf_label, count: 0, tasks: new Set() };
    tally.leaves.set(run.leaf, leaf);
  }
  leaf.count += 1;
  leaf.tasks.add(run.task_slug);
}

/** Tallies -> sorted share rows over the model-failure denominator. */
function shapeFamilies(
  tallies: Map<string, FamilyTally>,
  denominator: number,
): AttributionFamilyShare[] {
  return Array.from(tallies.entries())
    .map(([family, tally]) => ({
      family,
      label: tally.label,
      count: tally.count,
      share: tally.count / denominator,
      leaves: Array.from(tally.leaves.entries())
        .map(([leaf, entry]) => ({
          leaf,
          label: entry.label,
          count: entry.count,
          taskCount: entry.tasks.size,
          share: entry.count / denominator,
        }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function attributionBreakdown(
  attributions: BenchmarkAttribution[],
  model: string | null,
): AttributionBreakdown {
  const scoped =
    model === null
      ? attributions
      : attributions.filter((a) => a.model === model);
  const tallies = new Map<string, FamilyTally>();
  const nonModelTallies = new Map<
    string,
    { label: string; count: number; tasks: Set<string> }
  >();
  let modelFailureCount = 0;
  let nonModelIssueCount = 0;
  let unknownCount = 0;
  for (const run of scoped) {
    if (run.top_level === "model_failure") {
      modelFailureCount += 1;
      tallyModelFailure(tallies, run);
    } else if (run.top_level === "non_model_issue") {
      nonModelIssueCount += 1;
      let tally = nonModelTallies.get(run.non_model_issue);
      if (tally === undefined) {
        tally = {
          label: run.non_model_issue_label,
          count: 0,
          tasks: new Set(),
        };
        nonModelTallies.set(run.non_model_issue, tally);
      }
      tally.count += 1;
      tally.tasks.add(run.task_slug);
    } else if (run.top_level === "unknown_or_insufficient_evidence") {
      unknownCount += 1;
    }
    // not_applicable = passed/ungradable runs: not failures, only in runCount.
  }
  return {
    families: shapeFamilies(tallies, modelFailureCount),
    nonModelIssues: Array.from(nonModelTallies.entries())
      .map(([leaf, tally]) => ({
        leaf,
        label: tally.label,
        count: tally.count,
        taskCount: tally.tasks.size,
        share: tally.count / nonModelIssueCount,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    modelFailureCount,
    nonModelIssueCount,
    unknownCount,
    runCount: scoped.length,
    totalRunCount: attributions.length,
  };
}

/** Mean score per section (task headline scores averaged), sorted by section. */
export function sectionScores(
  tasks: BenchmarkTask[],
  analysisTasks: AnalysisTask[],
): { section: string; score: number }[] {
  const sectionByTask = new Map(tasks.map((task) => [task.slug, task.section]));
  const bySection = new Map<string, number[]>();
  for (const task of analysisTasks) {
    if (task.score === null) continue;
    const section = sectionByTask.get(task.slug);
    if (section === undefined || section === "") continue;
    const scores = bySection.get(section);
    if (scores === undefined) {
      bySection.set(section, [task.score]);
    } else {
      scores.push(task.score);
    }
  }
  return Array.from(bySection.entries())
    .map(([section, scores]) => {
      const sectionMean = mean(scores);
      if (sectionMean === null) {
        throw new Error(`unreachable: section ${section} collected no scores`);
      }
      return { section, score: sectionMean };
    })
    .sort((a, b) => a.section.localeCompare(b.section));
}

/** Which score statistic a distribution line plots. */
export type ScoreSeries = "best" | "mean" | "worst";

export const SERIES_ORDER: ScoreSeries[] = ["best", "mean", "worst"];

export const SERIES_LABELS: Record<ScoreSeries, string> = {
  best: "Highest",
  mean: "Average",
  worst: "Lowest",
};

/** The two pass-metric curves the chart can overlay (TRA-831 follow-up):
 * pass@k = P(at least one of k sampled rollouts passes) — capability;
 * pass^k = P(all k sampled rollouts pass) — reliability. */
export type PassMetric = "pass_at" | "pass_hat";

export const PASS_METRIC_ORDER: PassMetric[] = ["pass_at", "pass_hat"];

export function passMetricLabel(metric: PassMetric, k: number): string {
  return metric === "pass_at" ? `pass@${k.toString()}` : `pass^${k.toString()}`;
}

/**
 * Unbiased pass@k estimator (Codex-paper form): with n rollouts of which
 * c passed, 1 - C(n-c, k)/C(n, k), computed as a running product so no
 * factorial overflows. k is clamped to n — a task with fewer rollouts
 * than k is estimated from all the rollouts it has.
 */
export function passAtK(n: number, c: number, k: number): number {
  const draws = Math.min(k, n);
  if (n - c < draws) return 1;
  let allMiss = 1;
  for (let i = 0; i < draws; i++) {
    allMiss *= (n - c - i) / (n - i);
  }
  return 1 - allMiss;
}

/** Unbiased pass^k estimator: C(c, k)/C(n, k) — see passAtK for clamping. */
export function passHatK(n: number, c: number, k: number): number {
  const draws = Math.min(k, n);
  if (c < draws) return 0;
  let allPass = 1;
  for (let i = 0; i < draws; i++) {
    allPass *= (c - i) / (n - i);
  }
  return allPass;
}

/**
 * Per-metric score lookups shaped like seriesScoreFns on BenchmarkPage:
 * (task, model) reads that model's row; (task, null) averages the
 * estimate across the task's models — matching the all-models mean
 * convention of the Average statistic.
 */
export function passScoreFns(
  scores: BenchmarkScore[],
  k: number,
): Record<
  PassMetric,
  (taskSlug: string, model: string | null) => number | null
> {
  const rowsByTask = new Map<string, BenchmarkScore[]>();
  const rowByTaskModel = new Map<string, BenchmarkScore>();
  for (const score of scores) {
    if (score.n_rollouts === 0) continue;
    rowByTaskModel.set(`${score.task_slug}|${score.model}`, score);
    const rows = rowsByTask.get(score.task_slug);
    if (rows === undefined) {
      rowsByTask.set(score.task_slug, [score]);
    } else {
      rows.push(score);
    }
  }
  const estimate = (metric: PassMetric, row: BenchmarkScore): number =>
    metric === "pass_at"
      ? passAtK(row.n_rollouts, row.pass_count, k)
      : passHatK(row.n_rollouts, row.pass_count, k);
  const lookup =
    (metric: PassMetric) =>
    (taskSlug: string, model: string | null): number | null => {
      if (model !== null) {
        const row = rowByTaskModel.get(`${taskSlug}|${model}`);
        return row === undefined ? null : estimate(metric, row);
      }
      const rows = rowsByTask.get(taskSlug);
      if (rows === undefined) return null;
      return mean(rows.map((row) => estimate(metric, row)));
    };
  return { pass_at: lookup("pass_at"), pass_hat: lookup("pass_hat") };
}

const seriesStorageKey = (benchmarkSlug: string): string =>
  `benchmark-series:${benchmarkSlug}`;

/** Selected series for a benchmark — persisted so the eval card matches. */
export function loadSeriesSelection(benchmarkSlug: string): ScoreSeries[] {
  const raw = readStoredValue(seriesStorageKey(benchmarkSlug));
  if (raw === null) return ["best"];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed.every((entry): entry is ScoreSeries =>
        SERIES_ORDER.includes(entry as ScoreSeries),
      )
    ) {
      return SERIES_ORDER.filter((series) => parsed.includes(series));
    }
    console.error(
      `invalid series selection in localStorage for ${benchmarkSlug}:`,
      raw,
    );
  } catch (error) {
    console.error(
      `unparseable series selection in localStorage for ${benchmarkSlug}:`,
      error,
    );
  }
  return ["best"];
}

export function saveSeriesSelection(
  benchmarkSlug: string,
  series: ScoreSeries[],
): void {
  writeStoredValue(seriesStorageKey(benchmarkSlug), JSON.stringify(series));
}

const GRID_BENCHMARK_COLUMNS = [
  "benchmark_slug",
  "benchmark_name",
  "benchmark_description",
  "benchmark_source",
  "generated_at",
  "benchmark_mean_score",
] as const;

const GRID_TASK_COLUMNS = [
  "task_slug",
  "task_name",
  "section",
  "world",
  "prompt",
  "rubric",
  "output_type",
  "required_skills",
  "fairness",
  "model_failure",
  "model_failure_detail",
  "failed_step",
  "verifier_status",
  "verifier_issue",
  "verifier_issue_detail",
  "training_readiness",
  "gold_response",
  "gold_response_type",
  "task_mean_score",
  "difficulty",
  "section_mean_score",
] as const;

const GRID_PASS_KS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

const GRID_SCORE_COLUMNS = [
  "model",
  "mean_score",
  "best_score",
  "worst_score",
  "n_rollouts",
  "pass_count",
  "partial_count",
  "fail_count",
  "step_rewards",
  "failure_types",
  ...GRID_PASS_KS.map((k) => `pass_at_${k.toString()}`),
  ...GRID_PASS_KS.map((k) => `pass_hat_${k.toString()}`),
];

/**
 * Per-(task, model) failure summary from the per-run attribution rows,
 * e.g. "Calculation x2; Tool or environment issue x1". Mirrors the
 * Failure types panel's labels: leaf for model failures, issue label
 * for non-model, top_level as the fallback for unlabeled rows.
 */
function failureSummaries(
  attributions: BenchmarkAttribution[],
): Map<string, string> {
  const counts = new Map<string, Map<string, number>>();
  for (const row of attributions) {
    if (row.run_outcome === "passed") continue;
    const label =
      row.leaf_label !== ""
        ? row.leaf_label
        : row.non_model_issue_label !== ""
          ? row.non_model_issue_label
          : row.top_level;
    const key = `${row.task_slug}|${row.model}`;
    const perLabel = counts.get(key) ?? new Map<string, number>();
    perLabel.set(label, (perLabel.get(label) ?? 0) + 1);
    counts.set(key, perLabel);
  }
  return new Map(
    [...counts].map(([key, perLabel]) => [
      key,
      [...perLabel]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([label, count]) => `${label} x${count.toString()}`)
        .join("; "),
    ]),
  );
}

/**
 * The benchmark grid as CSV — one row per (task, model) score, joined
 * with the manifest header, every task-table column, the failure-type
 * summary, and the same derived stats the page shows (task headline,
 * difficulty, section mean, benchmark mean, pass@k and pass^k for
 * k = 1..8). Tasks are the spine (matching toAnalysisTasks): a
 * task with no scores still gets one row with blank score cells, so
 * the export covers the whole grid.
 */
export function benchmarkGridCsv(
  manifest: BenchmarkManifest,
  tasks: BenchmarkTask[],
  scores: BenchmarkScore[],
  attributions: BenchmarkAttribution[],
): string {
  const failures = failureSummaries(attributions);
  const byTask = new Map<string, BenchmarkScore[]>();
  for (const score of scores) {
    const rows = byTask.get(score.task_slug);
    if (rows === undefined) {
      byTask.set(score.task_slug, [score]);
    } else {
      rows.push(score);
    }
  }
  // Derived stats, matching the page: headline = mean of model means,
  // difficulty from the headline, section/benchmark means over scored
  // tasks only.
  const headlines = new Map<string, number>();
  const bySection = new Map<string, number[]>();
  for (const task of tasks) {
    const headline = mean(
      (byTask.get(task.slug) ?? []).map((score) => score.mean_score),
    );
    if (headline === null) continue;
    headlines.set(task.slug, headline);
    if (task.section === "") continue;
    const sectionHeadlines = bySection.get(task.section) ?? [];
    sectionHeadlines.push(headline);
    bySection.set(task.section, sectionHeadlines);
  }
  const benchmarkMean = mean([...headlines.values()]);
  const benchmarkCells = [
    manifest.slug,
    manifest.display_name,
    manifest.description,
    manifest.source,
    manifest.generated_at,
    benchmarkMean ?? "",
  ];
  const rows = tasks.flatMap((task) => {
    const headline = headlines.get(task.slug) ?? null;
    const sectionMean =
      task.section === "" ? null : mean(bySection.get(task.section) ?? []);
    const taskCells = [
      ...benchmarkCells,
      task.slug,
      task.name,
      task.section,
      task.world,
      task.prompt,
      JSON.stringify(task.rubric),
      task.output_type,
      task.required_skills.join("; "),
      task.fairness,
      task.model_failure,
      task.model_failure_detail,
      task.failed_step,
      task.verifier_status,
      task.verifier_issue,
      task.verifier_issue_detail,
      task.training_readiness,
      task.gold_response,
      task.gold_response_type,
      headline ?? "",
      deriveDifficulty(headline),
      sectionMean ?? "",
    ];
    const taskScores = byTask.get(task.slug) ?? [];
    if (taskScores.length === 0) {
      return [[...taskCells, ...GRID_SCORE_COLUMNS.map(() => "")]];
    }
    return taskScores.map((score) => [
      ...taskCells,
      score.model,
      score.mean_score,
      score.best_score,
      score.worst_score,
      score.n_rollouts,
      score.pass_count,
      score.partial_count,
      score.fail_count,
      score.step_rewards.join(" "),
      failures.get(`${task.slug}|${score.model}`) ?? "",
      ...GRID_PASS_KS.map((k) =>
        passAtK(score.n_rollouts, score.pass_count, k),
      ),
      ...GRID_PASS_KS.map((k) =>
        passHatK(score.n_rollouts, score.pass_count, k),
      ),
    ]);
  });
  return [
    [
      ...GRID_BENCHMARK_COLUMNS,
      ...GRID_TASK_COLUMNS,
      ...GRID_SCORE_COLUMNS,
    ].join(","),
    ...rows.map((row) => row.map(csvCell).join(",")),
  ].join("\n");
}
